// STYLES
import { useEffect, useState } from "react";
import * as Style from "./PushNotificationsStyle";

// LIBRARIES
import dayjs from "dayjs";
import Tooltip from "@material-ui/core/Tooltip";
import { useTranslation } from "react-i18next";

// MISC
import { ButtonTypes, InputTypes } from "models/GeneralTypes/generalTypes";
import {
  IPushNotificationDetails,
  IPushNotificationsSending,
} from "models/interfaces";

// ASSETS
import ClockIcon from "assets/Icons/TimeIcon.svg";

// REDUX
import {
  createNotification,
  getPushNotifications,
} from "api/pushNotificationsApi";
import { useAppDispatch } from "hooks/reduxHooks";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import InputAtom from "components/Atoms/InputAtom/InputAtom";

const PushNotifications = () => {
  // PROPS

  // CONSTANTS USING LIBRARIES
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // CONSTANTS USING HOOKS
  const [pushNotificationsSending, setPushNotificationsSending] =
    useState<IPushNotificationsSending>({
      title: "",
      body: "",
    });
  const [pushNotificationDetails, setPushNotificationDetails] =
    useState<IPushNotificationDetails>({
      content: [],
      pageable: null,
      totalPages: 0,
      totalElements: 0,
      last: false,
      size: 0,
      number: 0,
      sort: null,
      numberOfElements: 0,
      first: false,
      empty: true,
    });
  const [currentPage, setCurrentPage] = useState(1);

  // GENERAL CONSTANTS

  // USE EFFECT FUNCTION
  useEffect(() => {
    dispatch(getPushNotifications(0))
      .unwrap()
      .then((response) => {
        setPushNotificationDetails(response.data);
      });
  }, []);

  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const handleChangePushNotifications = (
    name: string,
    value: React.SetStateAction<any>
  ) => {
    setPushNotificationsSending({
      ...pushNotificationsSending,
      [name]: value,
    });
  };

  const handleSendPushNotification = () => {
    dispatch(createNotification(pushNotificationsSending))
      .unwrap()
      .then(() => {
        setPushNotificationsSending({
          title: "",
          body: "",
        });
        dispatch(getPushNotifications(0))
          .unwrap()
          .then((response) => {
            setPushNotificationDetails(response.data);
          });
      });
  };

  const handleScrollEnd = (e: React.UIEvent<HTMLDivElement>) => {
    const divElement = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = divElement;
    if (scrollTop + clientHeight === scrollHeight) {
      dispatch(getPushNotifications(currentPage))
        .unwrap()
        .then((response) => {
          setCurrentPage(currentPage + 1);
          setPushNotificationDetails({
            ...response.data,
            content: [
              ...pushNotificationDetails?.content,
              ...response.data.content,
            ],
          });
        });
    }
  };

  return (
    <Style.Container>
      <Style.SendNotificationsContainer>
        <Style.Title>
          {t("page.pushNotifications.title.notifications")}
        </Style.Title>
        <Style.Bar />
        <Style.TitleInputContainer>
          <Style.SendTitle>{t("page.pushNotifications.title")}</Style.SendTitle>
          <Style.InputAtomContainer width="300px">
            <InputAtom
              placeholder={"Scrie"}
              inputValue={pushNotificationsSending.title}
              setInputValue={(value) =>
                handleChangePushNotifications("title", value)
              }
              type={InputTypes.MODAL_INPUT}
            />
          </Style.InputAtomContainer>
        </Style.TitleInputContainer>
        <Style.ContentContainer>
          <Style.BodyContainer>
            <Style.BodyTitle>
              {t("page.pushNotifications.body")}
            </Style.BodyTitle>
            <Style.InputAtomContainer width="300px">
              <InputAtom
                placeholder={"Scrie"}
                inputValue={pushNotificationsSending.body}
                height="400px"
                multiline
                maxRows={16}
                setInputValue={(value) =>
                  handleChangePushNotifications("body", value)
                }
                type={InputTypes.MODAL_INPUT}
              />
            </Style.InputAtomContainer>
          </Style.BodyContainer>
          <Style.ButtonContainer>
            <ButtonAtom
              text={t("page.pushNotifications.button.title")}
              type={ButtonTypes.MAIN_BUTTON}
              onClick={handleSendPushNotification}
            />
          </Style.ButtonContainer>
        </Style.ContentContainer>
      </Style.SendNotificationsContainer>
      <Style.NotificationsListContainer>
        <Style.Title>
          {t("page.pushNotifications.title.notificationsList")}
        </Style.Title>
        <Style.Bar />
        <Style.NotificationsContainer onScroll={handleScrollEnd}>
          {pushNotificationDetails !== null &&
            pushNotificationDetails.content.map((pushNotificationDetails) => {
              return (
                <Style.NotificationContainer>
                  <Style.NotificationTitle>
                    {pushNotificationDetails.title}
                  </Style.NotificationTitle>
                  <Tooltip title={pushNotificationDetails.body} arrow>
                    <Style.NotificationBodyText>
                      {pushNotificationDetails.body}
                    </Style.NotificationBodyText>
                  </Tooltip>
                  <Style.NotificationDateContainer>
                    <Style.ClockIconImage src={ClockIcon} />
                    <Style.NotificationDate>
                      {dayjs(pushNotificationDetails.creationDate).format(
                        "HH:mm DD.MM.YYYY"
                      )}{" "}
                    </Style.NotificationDate>
                  </Style.NotificationDateContainer>
                  <Style.NotificationBar />
                </Style.NotificationContainer>
              );
            })}
        </Style.NotificationsContainer>
      </Style.NotificationsListContainer>
    </Style.Container>
  );
};

export default PushNotifications;
